import styled from "styled-components";
import { colors } from "../../../Shared/colors";

/**
 * Styled pieces for the employee removal wizard.
 *
 * Deliberately reuses the existing palette rather than introducing a new one:
 * this dialog opens from Manage Staff, and a screen that recolours itself
 * mid-flow reads as a different product. Density follows the data-dense
 * dashboard scale already used on the Social Media screen — 8px gaps, compact
 * rows, 12-14px type — because this is an admin tool, not a marketing page.
 */

/** Red for anything irreversible; nothing else on this screen may use it. */
export const DANGER = colors.veryHot;

export const Step = styled.div`
  padding: 18px 22px;
  min-height: 220px;
`;

export const StepHead = styled.div`
  margin-bottom: 14px;

  h3 {
    margin: 0 0 4px 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: ${colors.text};
  }
  p {
    margin: 0;
    font-size: 0.82rem;
    line-height: 1.5;
    color: ${colors.textMed};
  }
`;

/** Step 1 of 5 — progress must be visible in any multi-step flow. */
export const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px 0 22px;

  .bar {
    flex: 1;
    height: 3px;
    border-radius: 20px;
    background: ${colors.inputWhite};
    overflow: hidden;
  }
  .fill {
    display: block;
    height: 100%;
    border-radius: 20px;
    background: ${colors.brown};
    transition: width 220ms ease;
  }
  .label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.textLight};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

/** Two-column count grid. Collapses to one column under 520px. */
export const CountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 8px;
`;

export const CountCard = styled.div`
  border: 1px solid ${colors.inputBorderWhite};
  border-left: 3px solid ${(props) => props.$stripe || colors.brown};
  border-radius: 10px;
  padding: 11px 13px;
  background: ${colors.white};

  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .n {
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.$figure || colors.text};
  }
  .k {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${colors.textLight};
  }
  ul {
    list-style: none;
    margin: 9px 0 0 0;
    padding: 0;
  }
  li {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.78rem;
    line-height: 1.9;
    color: ${colors.textMed};
  }
  li span:last-child {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: ${colors.text};
  }
`;

/** Says plainly what will NOT move. The wording matters more than the styling. */
export const StaysNote = styled.p`
  margin: 12px 0 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.79rem;
  line-height: 1.55;
  color: ${colors.textMed};
  background: ${colors.lightWhite};
  border: 1px solid ${colors.inputBorderWhite};

  strong {
    color: ${colors.text};
  }
`;

export const Callout = styled.div`
  display: flex;
  gap: 10px;
  padding: 11px 13px;
  border-radius: 9px;
  font-size: 0.81rem;
  line-height: 1.55;
  border: 1px solid
    ${(props) => (props.$tone === "danger" ? "rgba(232,11,11,0.22)" : colors.inputBorderWhite)};
  background: ${(props) =>
    props.$tone === "danger" ? "rgba(232,11,11,0.06)" : colors.lightWhite};
  color: ${(props) => (props.$tone === "danger" ? DANGER : colors.textMed)};

  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
    margin-top: 1px;
  }
  strong {
    color: ${(props) => (props.$tone === "danger" ? DANGER : colors.text)};
  }
`;

/** Recipient picker. Rows are 44px tall so they are comfortable touch targets. */
export const PickList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 10px;

  li + li {
    border-top: 1px solid ${colors.inputBorderWhite};
  }
`;

export const PickRow = styled.label`
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 44px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${colors.lightWhite};
  }
  &:focus-within {
    outline: 2px solid ${colors.textBlue};
    outline-offset: -2px;
  }

  input {
    width: 17px;
    height: 17px;
    accent-color: ${colors.brown};
    cursor: pointer;
    flex-shrink: 0;
  }

  .who {
    flex: 1;
    min-width: 0;
  }
  .name {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${colors.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .role {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.textLight};
  }
  .load {
    font-size: 0.75rem;
    color: ${colors.textLight};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

export const Segmented = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

export const SegmentOption = styled.button`
  font-family: inherit;
  text-align: left;
  padding: 12px 14px;
  min-height: 44px;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;
  background: ${(props) => (props.$active ? colors.lightWhite : colors.white)};
  border: 1.5px solid
    ${(props) => (props.$active ? colors.brown : colors.inputBorderWhite)};

  &:hover {
    border-color: ${colors.brown};
  }
  &:focus-visible {
    outline: 2px solid ${colors.textBlue};
    outline-offset: 2px;
  }

  .t {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${colors.text};
    margin-bottom: 2px;
  }
  .d {
    display: block;
    font-size: 0.75rem;
    line-height: 1.45;
    color: ${colors.textMed};
  }
`;

export const Field = styled.div`
  margin-top: 14px;

  label {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${colors.textLight};
    margin-bottom: 6px;
  }
  select,
  input {
    width: 100%;
    font-family: inherit;
    font-size: 0.86rem;
    padding: 10px 12px;
    min-height: 44px;
    border-radius: 8px;
    border: 1px solid ${colors.inputBorderWhite};
    background: ${colors.white};
    color: ${colors.text};
  }
  select:focus-visible,
  input:focus-visible {
    outline: 2px solid ${colors.textBlue};
    outline-offset: -1px;
  }
  .hint {
    margin: 6px 0 0 0;
    font-size: 0.75rem;
    line-height: 1.45;
    color: ${colors.textLight};
  }
`;

export const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 13px 22px;
  border-top: 1px solid ${colors.inputBorderWhite};
  background: ${colors.lightWhite};
`;

const buttonBase = `
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 10px 18px;
  min-height: 44px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 160ms ease, background 160ms ease, border-color 160ms ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid #2D7AF4;
    outline-offset: 2px;
  }
`;

export const GhostBtn = styled.button`
  ${buttonBase}
  background: ${colors.white};
  color: ${colors.textMed};
  border: 1px solid ${colors.inputBorderWhite};

  &:hover:not(:disabled) {
    border-color: ${colors.brown};
    color: ${colors.brown};
  }
`;

export const NextBtn = styled.button`
  ${buttonBase}
  background: ${colors.brown};
  color: ${colors.white};
  border: 1px solid ${colors.brown};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

/** The only red button in the flow, and only on the final step. */
export const DangerBtn = styled.button`
  ${buttonBase}
  background: ${DANGER};
  color: ${colors.white};
  border: 1px solid ${DANGER};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;
